/**
 * @module  Experience/Softgoods
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */
/* tslint:disable:no-any */ // We need to be able to allow developers send any payload the want
import { Injectable } from '@angular/core';
import { FamilyPlanResponse } from '@experience/shop/softgoods/interfaces/family-plan.interface';
import { ApiGatewayService } from '@core/api/services/api-gateway-service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { APIError } from '@support/api/common/error.interface';
import { BrowsePlansRequestModel } from '@experience/shared-experience/models/browse-plans-request.model';
import { BrowsePlansResponseModel } from '@experience/shared-experience/models/browse-plans-response.model';
import { API_MAP } from '@core/api/proxies/common-api-endpoints';
import { SharedBrowseService } from '@experience/shop/shared-browse/services/shared-browse.service';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { PRODUCT_SELECTOR_CONSTANTS } from '@experience/shop/product-selector/constants/product-selector.constants';
import { ByodObject } from '@experience/shop/byod/interfaces/byod-landing.interface';
import { EndpointConfigInterface } from '@core/interfaces/endpoint-configuration.interface';
import { ORDER_TYPE } from '@experience/shop/shop.constants';
import { BrowseEntry } from '@experience/shop/shared-browse/interfaces/browse-entry.interface';
import { Address } from '@support/api/proxies/responses/api-2004.interface';
import { ActivatedRoute } from '@angular/router';
import { FLOW } from '@experience/shared-experience/shared-experience.constants';
import { ApiCache } from '@support/shared/interfaces/api-cache-object.interface';
import { of as observableOf, throwError as observableThrowError, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HttpResponse, HttpHeaders } from '@angular/common/http';
import { FamilyPlans } from '@support/api/proxies/responses/api-2117.interface';
import { RetailPlansData } from '@experience/shop/shared-browse/interfaces/plan-card-data.interface';

/**
 * Export service for Browse plans
 */
@Injectable({
  providedIn: 'root',
})
export class BrowsePlanServiceService {
  constructor(
    private apiGateway: ApiGatewayService,
    private stateStoreUtilsService: StateStoreUtilsService,
    private sharedBrowseService: SharedBrowseService,
    private objectService: ObjectService,
    private activatedRoute: ActivatedRoute
  ) {}

  // All plans code has been romoved as not in use now make common
  /***
   * Method to call Get Plans API
   *  @param url, requestBody, headers, pathParams, selectedLine, flow, lineId, cacheObject, isRetailBrowsePlan
   *  @returns Observable<FamilyPlanResponse>
   */
  public apiMethods(
    url: EndpointConfigInterface,
    requestBody: Object,
    headers: HttpHeaders,
    pathParams: { [name: string]: string },
    selectedLine: number,
    flow: string,
    lineId?: string,
    cacheObject?: ApiCache,
    isRetailBrowsePlan?: boolean
  ): Observable<Object> {
    return this.apiGateway
      .postRequest(url, requestBody, headers, null, pathParams, cacheObject)
      .pipe(
        map((response: HttpResponse<Object>) => {
          return this.getRatePlans(response, selectedLine, flow, lineId, isRetailBrowsePlan);
        }),
        catchError((error: APIError) => {
          return observableThrowError(error);
        })
      );
  }

  /***
   * Method to get rate plans
   *  @param response, selectedLine, flow, lineId, isBrowsePlan
   *  @returns family plan
   */
  public getRatePlans(
    response: HttpResponse<Object>,
    selectedLine: number,
    flow: string,
    lineId?: string,
    isBrowsePlan?: boolean
  ): FamilyPlanResponse | Observable<RetailPlansData> {
    const cart = this.stateStoreUtilsService.getCart();
    const selectedLineId = this.stateStoreUtilsService.getLineId();
    if (isBrowsePlan) {
      return <FamilyPlanResponse>response.body;
    } else {
      const familyPlanResponse = new BrowsePlansResponseModel(
        <FamilyPlans>response.body,
        cart,
        selectedLineId
      );
      const plans = familyPlanResponse.processFamilyResponse(selectedLine, flow, lineId);
      return plans;
    }
  }

  /**
   * Gets browse plans
   * @returns family plan
   */
  public getBrowsePlans(
    isProductSelector?: boolean,
    isAddLine?: boolean,
    selectedLine?: number,
    familyCode?: string,
    isCps?: boolean,
    isBillingAddressConflict?: boolean,
    isSegmentationConflict?: boolean,
    cacheObject?: ApiCache,
    isRetailBrowsePlan?: boolean
  ): Observable<FamilyPlanResponse | RetailPlansData> {
    /* TODO:  Commenting the below
    because how to get customerConfigData at the pageLoad which are mandatory for 2127 call?  */
    const customerConfigData = this.sharedBrowseService.getUserInputsConfigData();
    const storeZip = this.sharedBrowseService.getStoreZipCode();
    const isZipCodeAvailable = this.objectService.get(
      this.stateStoreUtilsService.getBillingAddress(),
      'postalCode'
    );
    if (
      !this.objectService.get(customerConfigData, 'zip') &&
      !isCps &&
      !storeZip &&
      !isZipCodeAvailable
    ) {
      return observableOf(null);
    } else {
      const lineId = this.stateStoreUtilsService.getLineId();
      const context =
        isBillingAddressConflict || isSegmentationConflict
          ? FLOW.CONFLICT
          : isCps
            ? FLOW.CPS
            : FLOW.PDP;
      const requestBody = this.getPlansRequest(
        isCps,
        selectedLine,
        customerConfigData,
        lineId,
        familyCode,
        isBillingAddressConflict
      );
      if (isRetailBrowsePlan) {
        requestBody.retailPlans = true;
      }
      return this.apiMethods(
        API_MAP.API_2117_retrievePlansForMultiLines,
        requestBody,
        new HttpHeaders(),
        null,
        selectedLine,
        context,
        lineId,
        cacheObject,
        isRetailBrowsePlan
      );
    }
  }

  /**
   * check entryPoint is Byod or Byos
   * @param ByodObject
   * @returns boolean
   */
  public byosCheck(entryPoint: string, byodData: ByodObject): boolean {
    return (
      entryPoint === PRODUCT_SELECTOR_CONSTANTS.BYOD_FIRST &&
      byodData &&
      byodData.simData &&
      !byodData.simData.isSimNotNeeded
    );
  }

  /**
   * create requestObject for plans api 2117
   * @param isCps
   * @param context
   * @param selectedLine
   * @param customerConfigData
   * @param lineId
   * @param familyCode
   * @param isBillingAddressConflict
   * @returns BrowsePlansRequestModel
   */
  private getPlansRequest(
    isCps: boolean,
    selectedLine: number,
    customerConfigData: BrowseEntry,
    lineId: string,
    familyCode: string,
    isBillingAddressConflict: boolean
  ): BrowsePlansRequestModel {
    let requestBody;
    const customerId = this.stateStoreUtilsService.getBscsCustomerID();
    const accountId: string = this.stateStoreUtilsService.getAddFan()
      ? ''
      : this.stateStoreUtilsService.getAccountId();
    const cartId: string = this.stateStoreUtilsService.getCartId();
    if (isBillingAddressConflict) {
      const customerAccounts = this.stateStoreUtilsService.getBillingBlockDetails();
      const newBillingAddress: Address = this.stateStoreUtilsService.getBillingAddress();
      const zip = this.objectService.get(newBillingAddress, 'postalCode');
      requestBody = new BrowsePlansRequestModel(
        customerId,
        accountId,
        customerAccounts.billingType,
        '',
        zip,
        '',
        '',
        cartId,
        lineId,
        null,
        '',
        [ORDER_TYPE.MANAGERATEPLAN]
      );
    } else if (isCps) {
      // Getting details from 2006 instead of 2007

      const customerAccounts = this.stateStoreUtilsService.getBillingBlockDetails();
      if (this.stateStoreUtilsService.getAddFan()) {
        requestBody = new BrowsePlansRequestModel(
          customerId,
          accountId,
          customerAccounts.billingType,
          '',
          customerAccounts.billingAddress.zip,
          '',
          '',
          cartId,
          lineId,
          null,
          '',
          [ORDER_TYPE.ADDALINE]
        );
      } else {
        requestBody = new BrowsePlansRequestModel(
          customerId,
          accountId,
          customerAccounts.billingType,
          '',
          customerAccounts.billingAddress.zip,
          '',
          '',
          cartId,
          lineId,
          null,
          '',
          [ORDER_TYPE.MANAGERATEPLAN]
        );
      }
    } else {
      requestBody = this.getPlanRequestForActivations(
        customerConfigData,
        selectedLine,
        customerId,
        accountId,
        cartId,
        familyCode
      );
    }
    return requestBody;
  }

  /**
   * create requestObject for plans api 2117 for activations
   * @param customerId
   * @param accountId
   * @param selectedLine
   * @param customerConfigData
   * @param cartId
   * @param familyCode
   * @returns BrowsePlansRequestModel
   */
  /* tslint:disable:cyclomatic-complexity */
  private getPlanRequestForActivations(
    customerConfigData: BrowseEntry,
    selectedLine: number,
    customerId: string,
    accountId: string,
    cartId: string,
    familyCode: string
  ): BrowsePlansRequestModel {
    let billingType;
    let creditLevel;
    if (customerConfigData) {
      billingType = this.sharedBrowseService.getBillingTypeAPIMapping(
        customerConfigData.billingType
      );
      creditLevel = this.sharedBrowseService.getCreditLevelAPIMapping(
        customerConfigData.creditLevel
      );
    }
    const byodData = this.stateStoreUtilsService.getByodRestoreModel();
    const entryPoint = this.stateStoreUtilsService.getEntryPoint();
    const startPoint: boolean = entryPoint === PRODUCT_SELECTOR_CONSTANTS.DEVICE_FIRST;
    let productType: string = startPoint ? PRODUCT_SELECTOR_CONSTANTS.DEVICE : null;
    const offerId = this.stateStoreUtilsService.getSelectedOfferId();
    const selectedItemDetails = this.stateStoreUtilsService.getSelectedDeviceDetails(offerId);
    let productSku: string = startPoint ? selectedItemDetails.skuId : null;
    if (this.byosCheck(entryPoint, byodData)) {
      if (byodData.simData.newSimData && byodData.simData.newSimData.simSKU) {
        productSku = byodData.simData.newSimData.simSKU;
        productType = PRODUCT_SELECTOR_CONSTANTS.SIM;
      }
    }
    const zip =
      this.objectService.get(this.stateStoreUtilsService.getBillingAddress(), 'postalCode') ||
      this.objectService.get(customerConfigData, 'zip') ||
      this.stateStoreUtilsService.getStoreZipCode();
    const isActivation: boolean = accountId === 'ANON' || accountId === '';
    const transactionType = isActivation
      ? PRODUCT_SELECTOR_CONSTANTS.ACTIVATION
      : PRODUCT_SELECTOR_CONSTANTS.ADDALINE;
    const voucher = this.activatedRoute.snapshot.queryParams.voucher;
    const requestBody = new BrowsePlansRequestModel(
      customerId,
      accountId,
      billingType,
      creditLevel,
      zip,
      productType,
      productSku,
      cartId,
      null,
      selectedLine,
      familyCode,
      [transactionType],
      voucher
    );
    return requestBody;
  }
}
